import { ContentText, TextPair } from '@dealstack/frontend/shared/components/TextPair/TextPair';
import {
	AlertDescription,
	AlertTitle,
	Alert,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { Expandable } from '@dealstack/frontend/shared/components/Expandable/Expandable';
import { useFiles } from '@dealstack/frontend/admin/pages/Manage/Files/useFiles';
import {
	Box,
	Button,
	Flex,
	Grid,
	Group,
	rem,
	Space,
	Text,
	TextInput,
	Title,
	FlexProps,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconPhoto, IconUpload } from '@tabler/icons-react';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const ManageFiles = () => {
	const manageFilesController = useFiles();

	return (
		<Box p="2rem">
			<BackLink to="/dashboard">
				<Button size="md" variant="outline">
					Back to dashboard
				</Button>
			</BackLink>
			<Grid>
				<Grid.Col span={6}>
					<Expandable initialOpened title="Upload file">
						<Alert icon="information-filled" state="warning" size="m">
							<AlertTitle>WARNING</AlertTitle>
							<AlertDescription>
								Files you upload here are created on S3 but you need to manually
								insert them into database. Only for developers with direct access to
								database.
							</AlertDescription>
						</Alert>
						<Space h="1rem" />
						<Dropzone
							onDrop={manageFilesController.onDropFiles}
							maxSize={3 * 1024 ** 2}
						>
							<Flex direction="column" align="center">
								<Group
									justify="center"
									gap="xl"
									style={{ minHeight: rem(220), pointerEvents: 'none' }}
								>
									<Dropzone.Accept>
										<IconUpload size="3.2rem" stroke={1.5} />
									</Dropzone.Accept>
									<Dropzone.Idle>
										<IconPhoto size="3.2rem" stroke={1.5} />
									</Dropzone.Idle>
									<div>
										<Text size="xl" inline>
											Drag files here or click to select files
										</Text>
									</div>
								</Group>
							</Flex>
						</Dropzone>
						{manageFilesController?.isUploadSuccess &&
						manageFilesController?.percentComplete === 100 ? (
							<ResultBox direction="column" align="left">
								<Title order={4}>Result</Title>
								<Space h="1rem" />
								<TextPair title="ID">
									<ContentText variant="sans/14/400">
										{
											manageFilesController?.uploadFileRequest?.response
												?.payload?.id
										}
									</ContentText>
								</TextPair>
								<Space h="1rem" />
								<TextPair title="Name">
									<ContentText variant="sans/14/400">
										{
											manageFilesController?.uploadFileRequest?.response
												?.payload?.name
										}
									</ContentText>
								</TextPair>
							</ResultBox>
						) : null}
					</Expandable>
				</Grid.Col>
				<Grid.Col span={6}>
					<Expandable initialOpened title="Download file">
						<Grid align="flex-end" justify="flex-start">
							<Grid.Col span={12}>
								<Flex gap="0.5rem" align="flex-end" justify="flex-start">
									<TextInput
										onChange={(event) =>
											manageFilesController.setDownloadFileIdValue(
												event?.currentTarget?.value
											)
										}
										placeholder="S3 File ID"
										label="S3 File ID"
									/>
									<Button onClick={manageFilesController.onFindFile}>
										Find file
									</Button>
								</Flex>
							</Grid.Col>
							{manageFilesController.fileData ? (
								<>
									<Grid.Col span={12}>
										<Flex gap="0.5rem">
											<Button onClick={manageFilesController.onDownload}>
												Download
											</Button>
										</Flex>
									</Grid.Col>
									<Grid.Col span={12}>
										<ResultBox direction="column" align="left">
											<Title order={4}>Result</Title>
											<Space h="1rem" />
											<TextPair title="File name">
												<ContentText variant="sans/14/400">
													{manageFilesController?.fileData?.fileName}
												</ContentText>
											</TextPair>
											<Space h="1rem" />
											<TextPair title="File extension">
												<ContentText variant="sans/14/400">
													{manageFilesController?.fileData?.fileExtension}
												</ContentText>
											</TextPair>
										</ResultBox>
									</Grid.Col>
								</>
							) : null}
						</Grid>
					</Expandable>
				</Grid.Col>
			</Grid>
		</Box>
	);
};

const BackLink = styled(Link)`
	display: block;
	margin-bottom: 2em;
`;

const ResultBox = styled(Flex)<FlexProps>`
	border: 2px dashed #ff3e17;
	padding: 2rem 2rem;
`;
export const Files = memo(ManageFiles);
